/*
  The small marks OnHand draws inside its own controls: a chevron, a tick, an
  arrow, a close cross, a repeat.

  **Made 11 September 2026, phase 6 of the brand rollout**, to replace typed
  characters. Until that day a chevron on a dropdown was the character `>` in
  disguise: U+25BE, U+2713, U+2190 and their literal cousins were scattered
  across sixteen screens.

  **Three things are wrong with a typed symbol.**

  1. **The brand guide's section 0 rule 12 rules them out**: a delivered file
     stays plain ASCII, no symbols. An HTML escape is the same symbol spelled
     differently, and it slipped a search for non-ASCII characters for exactly
     that reason.
  2. **It draws at whatever size and weight the reader's fallback face gives
     it.** Manrope and Inter do not carry a black down-pointing triangle, so it
     came from whatever the device had, which is not the same on a phone as on
     a laptop and is not the brand's weight anywhere.
  3. **A screen reader reads it out.** "Black down-pointing small triangle" in
     the middle of a sentence is not what the sentence says, which is why every
     one of these carries `aria-hidden`.

  **The stroke matches `ICON` in `src/lib/menu.jsx`**, which is the app's own
  hand-drawn set: 24 by 24, no fill, round caps and joins. The guide's section
  10 names Lucide, and `lucide-react` is not installed; adding it would change
  `package.json`, which is what makes Netlify publish the website, so that is a
  decision for the owner rather than a side effect of a copy pass.

  **They take their colour from the text around them**, which is what
  `currentColor` means, so a chevron inside a blue link is blue and the same
  chevron in a grey row is grey, with nothing restated in either place.
*/
import React from 'react';

function Mark({ size = 12, children, style, className }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false"
      className={className}
      /* **`inline-block`, not `block`.** These sit inside sentences as often as
         inside icon-only buttons: "View locations", "Open the Review Hub",
         "Back to Dashboard". A block-level mark inside a centred button takes
         a line of its own and lands at the left edge, which is exactly what it
         did on three card footers before anybody opened the picture. */
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: '-0.12em', ...style }}
    >
      {children}
    </svg>
  );
}

/*
  A chevron. `dir` turns it: down by default, and up, left or right on request.

  **It turns with a transition** so a disclosure that opens does not appear to
  jump, which is what the characters it replaced were doing with a `transform`
  of their own. Honour reduced motion: the transition is named in `index.css`
  where the reduced-motion rule can reach it.
*/
const TURN = { down: 0, up: 180, left: 90, right: -90 };

export function Chevron({ dir = 'down', size = 12, style, className }) {
  return (
    <Mark size={size} className={className}
      style={{ transform: `rotate(${TURN[dir] || 0}deg)`, transition: 'transform var(--oh-dur-fast) var(--oh-ease)', ...style }}>
      <path d="M6 9l6 6 6-6" />
    </Mark>
  );
}

export function Tick({ size = 14, style, className }) {
  return <Mark size={size} style={style} className={className}><path d="M20 6L9 17l-5-5" /></Mark>;
}

export function Close({ size = 14, style, className }) {
  return (
    <Mark size={size} style={style} className={className}>
      <path d="M18 6L6 18" /><path d="M6 6l12 12" />
    </Mark>
  );
}

export function ArrowLeft({ size = 14, style, className }) {
  return (
    <Mark size={size} style={style} className={className}>
      <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
    </Mark>
  );
}

export function ArrowRight({ size = 14, style, className }) {
  return (
    <Mark size={size} style={style} className={className}>
      <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
    </Mark>
  );
}

// Try again. It stood in for a typed U+21BB on the barcode scanner.
export function Repeat({ size = 22, style, className }) {
  return (
    <Mark size={size} style={style} className={className}>
      <path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" />
    </Mark>
  );
}
